import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import type { BankAccount, SharedBankAccount } from '../../types';

interface AccountMultiSelectProps {
  personalAccounts: BankAccount[];
  sharedAccounts: SharedBankAccount[];
  selectedPersonalIds: string[];
  selectedSharedIds: string[];
  onChange: (personalIds: string[], sharedIds: string[]) => void;
  disabled?: boolean;
}

export default function AccountMultiSelect({
  personalAccounts,
  sharedAccounts,
  selectedPersonalIds,
  selectedSharedIds,
  onChange,
  disabled,
}: AccountMultiSelectProps) {
  const { t } = useTranslation('search');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const allPersonalSelected = personalAccounts.length > 0 && selectedPersonalIds.length === personalAccounts.length;
  const allSharedSelected = sharedAccounts.length > 0 && selectedSharedIds.length === sharedAccounts.length;

  const togglePersonal = (id: string) => {
    if (selectedPersonalIds.includes(id)) {
      onChange(selectedPersonalIds.filter((x) => x !== id), selectedSharedIds);
    } else {
      onChange([...selectedPersonalIds, id], selectedSharedIds);
    }
  };

  const toggleShared = (id: string) => {
    if (selectedSharedIds.includes(id)) {
      onChange(selectedPersonalIds, selectedSharedIds.filter((x) => x !== id));
    } else {
      onChange(selectedPersonalIds, [...selectedSharedIds, id]);
    }
  };

  const toggleAllPersonal = () => {
    if (allPersonalSelected) {
      onChange([], selectedSharedIds);
    } else {
      onChange(personalAccounts.map((a) => a.id), selectedSharedIds);
    }
  };

  const toggleAllShared = () => {
    if (allSharedSelected) {
      onChange(selectedPersonalIds, []);
    } else {
      onChange(selectedPersonalIds, sharedAccounts.map((a) => a.id));
    }
  };

  const clearAll = () => onChange([], []);

  const selectedCount = selectedPersonalIds.length + selectedSharedIds.length;
  const totalCount = personalAccounts.length + sharedAccounts.length;

  const summary = useMemo(() => {
    if (selectedCount === 0) return t('filters.allAccounts');
    if (selectedCount === totalCount) return t('filters.allAccounts');
    if (selectedCount === 1) {
      const p = personalAccounts.find((a) => a.id === selectedPersonalIds[0]);
      const s = sharedAccounts.find((a) => a.id === selectedSharedIds[0]);
      return p?.name || s?.name || '';
    }
    return t('filters.accountsCount', { count: selectedCount });
  }, [selectedCount, totalCount, personalAccounts, sharedAccounts, selectedPersonalIds, selectedSharedIds, t]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className={clsx(
          'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border bg-white text-sm transition-colors',
          disabled
            ? 'border-gray-200 text-gray-400 cursor-not-allowed'
            : 'border-gray-300 text-gray-700 hover:border-primary-400',
        )}
      >
        <span className="truncate">{summary}</span>
        <div className="flex items-center gap-1 shrink-0">
          {selectedCount > 0 && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); clearAll(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); clearAll(); } }}
              className="p-0.5 hover:bg-gray-100 rounded"
            >
              <X className="w-3.5 h-3.5 text-gray-500" />
            </span>
          )}
          <ChevronDown className={clsx('w-4 h-4 text-gray-400 transition-transform', open && 'rotate-180')} />
        </div>
      </button>

      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {sharedAccounts.length > 0 && (
            <div>
              <button
                type="button"
                onClick={toggleAllShared}
                className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-600 border-b border-gray-100"
              >
                <span>{t('filters.sharedSection')}</span>
                {allSharedSelected && <Check className="w-3.5 h-3.5 text-primary-600" />}
              </button>
              {sharedAccounts.map((account) => {
                const checked = selectedSharedIds.includes(account.id);
                return (
                  <button
                    key={account.id}
                    type="button"
                    onClick={() => toggleShared(account.id)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left"
                  >
                    <span
                      className={clsx(
                        'w-4 h-4 rounded border flex items-center justify-center shrink-0',
                        checked ? 'bg-primary-600 border-primary-600' : 'border-gray-300',
                      )}
                    >
                      {checked && <Check className="w-3 h-3 text-white" />}
                    </span>
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: account.color }} />
                    <span className="truncate text-gray-700">{account.name}</span>
                  </button>
                );
              })}
            </div>
          )}

          {personalAccounts.length > 0 && (
            <div>
              <button
                type="button"
                onClick={toggleAllPersonal}
                className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-600 border-b border-gray-100"
              >
                <span>{t('filters.personalSection')}</span>
                {allPersonalSelected && <Check className="w-3.5 h-3.5 text-primary-600" />}
              </button>
              {personalAccounts.map((account) => {
                const checked = selectedPersonalIds.includes(account.id);
                return (
                  <button
                    key={account.id}
                    type="button"
                    onClick={() => togglePersonal(account.id)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left"
                  >
                    <span
                      className={clsx(
                        'w-4 h-4 rounded border flex items-center justify-center shrink-0',
                        checked ? 'bg-primary-600 border-primary-600' : 'border-gray-300',
                      )}
                    >
                      {checked && <Check className="w-3 h-3 text-white" />}
                    </span>
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: account.color }} />
                    <span className="truncate text-gray-700">{account.name}</span>
                  </button>
                );
              })}
            </div>
          )}

          {sharedAccounts.length === 0 && personalAccounts.length === 0 && (
            <div className="px-3 py-3 text-sm text-gray-500 text-center">
              {t('filters.noAccounts')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
